import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';

/** Estados compactos: menos peso visual cuando hay muchas celdas iguales */
const CeldaParte = ({ celda, onClick }) => {
  if (!celda || celda.tipo === 'vacío') {
    return <span className="text-muted laboral-celda-vacio">—</span>;
  }
  const propsClick = celda?.detalle
    ? {
        role: 'button',
        tabIndex: 0,
        onClick: () => onClick?.(celda.detalle),
        onKeyDown: (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick?.(celda.detalle);
          }
        },
        className: 'laboral-celda-clickable',
      }
    : {};
  if (celda.tipo === 'completado') {
    return (
      <span {...propsClick}>
        <abbr title="Completado" className="laboral-dot laboral-dot--ok d-inline-flex align-items-center justify-content-center">
          ✓
        </abbr>
      </span>
    );
  }
  if (celda.tipo === 'sin_asignar') {
    return (
      <span {...propsClick}>
        <abbr title="Sin asignar" className="laboral-dot laboral-dot--danger d-inline-flex align-items-center justify-content-center">
          !
        </abbr>
      </span>
    );
  }
  if (celda.tipo === 'asignado') {
    const n = celda.nombreCorto || '—';
    return (
      <span {...propsClick}>
        <span className="laboral-pill-asignado" title="Pendiente / en curso (asignado)">
          {n}
        </span>
      </span>
    );
  }
  return <span className="text-muted small">—</span>;
};

function filaCoincideBusqueda(row, termino) {
  if (!termino) return true;
  const t = termino.toLowerCase();
  if (row.numero && String(row.numero).toLowerCase().includes(t)) return true;
  if (row.clientePrimerNombre && String(row.clientePrimerNombre).toLowerCase().includes(t)) return true;
  const celdas = row.celdas || {};
  for (const key of Object.keys(celdas)) {
    const c = celdas[key];
    if (c.tipo === 'asignado' && c.nombreCorto && String(c.nombreCorto).toLowerCase().includes(t)) {
      return true;
    }
    if (c.tipo === 'completado' && 'completado'.includes(t)) return true;
    if (c.tipo === 'sin_asignar' && 'sin'.includes(t)) return true;
  }
  return false;
}

/** Encabezado corto + tooltip con nombre completo de la parte */
function tituloColumnaParte(col) {
  if (!col) return '';
  if (col.length <= 12) return col;
  return `${col.slice(0, 10)}…`;
}

const TablaLaboral = ({ data, loading }) => {
  const [busqueda, setBusqueda] = useState('');
  const [detalleParte, setDetalleParte] = useState(null);

  const fmtFecha = (d) =>
    d
      ? new Date(d).toLocaleDateString('es-PE', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          timeZone: 'UTC',
        })
      : '—';

  const porTipoFiltrado = useMemo(() => {
    if (!data?.porTipo?.length) return [];
    const termino = busqueda.trim();
    return data.porTipo
      .map((grupo) => ({
        ...grupo,
        filas: (grupo.filas || []).filter((row) => filaCoincideBusqueda(row, termino))
      }))
      .filter((g) => g.filas.length > 0);
  }, [data, busqueda]);

  useEffect(() => {
    if (!detalleParte) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') setDetalleParte(null);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [detalleParte]);

  const modalDetalle = detalleParte ? (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="laboral-detalle-title"
      className="laboral-detalle-overlay"
      onClick={() => setDetalleParte(null)}
    >
      <div
        role="document"
        className="laboral-detalle-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header py-2">
          <h6 id="laboral-detalle-title" className="modal-title mb-0">
            Detalle de parte
          </h6>
          <button
            type="button"
            className="btn-close"
            aria-label="Cerrar"
            onClick={() => setDetalleParte(null)}
          />
        </div>
        <div className="modal-body py-2">
          <div className="small"><strong>Parte:</strong> {detalleParte.descripcion || '—'}</div>
          <div className="small"><strong>Asignado a:</strong> {detalleParte.asignadoNombre || 'Sin asignar'}</div>
          <div className="small"><strong>Fecha asignación:</strong> {fmtFecha(detalleParte.fechaAsignacion)}</div>
          <div className="small"><strong>Fecha límite:</strong> {fmtFecha(detalleParte.fechaLimite)}</div>
          <div className="small"><strong>Fecha entrega:</strong> {fmtFecha(detalleParte.fechaCompletado)}</div>
          <div className="small mb-2"><strong>Estado:</strong> {detalleParte.estado || '—'}</div>
          <Link
            className="btn btn-sm btn-outline-success"
            to={`/expedientes/${detalleParte.expedienteId}?tab=archivos&parte=${encodeURIComponent(
              (detalleParte.descripcion || '').trim().replace(/\s+/g, '_')
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setDetalleParte(null)}
          >
            <i className="fas fa-folder-open me-1" />
            Ir a carpeta de archivo
          </Link>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div className="vista-laboral-matriz">
      <style>{`
        .laboral-matriz-scroll {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          border-radius: 0 0 0.375rem 0.375rem;
        }
        .laboral-matriz-scroll .laboral-matriz-table {
          border: 1px solid #dee2e6;
        }
        .laboral-matriz-table {
          --laboral-w-num: 124px;
          --laboral-w-nom: 168px;
          table-layout: auto;
          border-collapse: separate;
          border-spacing: 0;
        }
        .laboral-matriz-table thead th {
          font-size: 0.68rem;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          font-weight: 600;
          color: #495057;
          border-bottom: 2px solid #dee2e6 !important;
          padding: 0.5rem 0.35rem;
          vertical-align: bottom;
        }
        .laboral-matriz-table tbody td {
          padding: 0.35rem 0.35rem;
          font-size: 0.8125rem;
          vertical-align: middle;
        }
        /*
          global.css: regla .table td (position relative, z-index 1) gana a .laboral-sticky-* sola;
          anula position:sticky en tbody y el borde de Nombre no coincide con el th. Forzar sticky.
        */
        .laboral-matriz-table.table > thead > tr > th.laboral-sticky-num,
        .laboral-matriz-table.table > tbody > tr > td.laboral-sticky-num {
          position: sticky !important;
          z-index: 3;
        }
        .laboral-matriz-table.table > thead > tr > th.laboral-sticky-nom,
        .laboral-matriz-table.table > tbody > tr > td.laboral-sticky-nom {
          position: sticky !important;
          z-index: 3;
        }
        .laboral-matriz-table.table > thead > tr > th.laboral-sticky-num,
        .laboral-matriz-table.table > thead > tr > th.laboral-sticky-nom {
          z-index: 4;
        }
        /* Columnas fijas: ancho explícito + borde derecho visible (separación clara) */
        .laboral-sticky-num {
          left: 0;
          min-width: var(--laboral-w-num);
          max-width: var(--laboral-w-num);
          width: var(--laboral-w-num);
          background: #fff;
          border-right: 2px solid #ced4da !important;
          box-shadow: 4px 0 12px rgba(0,0,0,0.06);
        }
        .laboral-sticky-nom {
          left: var(--laboral-w-num);
          min-width: var(--laboral-w-nom);
          width: var(--laboral-w-nom);
          max-width: 200px;
          background: #fff;
          border-right: 2px solid #ced4da !important;
          box-shadow: 4px 0 12px rgba(0,0,0,0.06);
          white-space: normal;
          word-break: break-word;
          line-height: 1.15;
          font-size: 0.72rem;
          text-align: center;
        }
        .laboral-matriz-table thead .laboral-sticky-num,
        .laboral-matriz-table thead .laboral-sticky-nom {
          background: #f1f3f5;
        }
        .laboral-td-parte {
          white-space: nowrap;
          text-align: center !important;
          vertical-align: middle;
          min-width: 52px;
          max-width: 72px;
          background: #fafbfc;
          border-left: 1px solid #e9ecef !important;
        }
        .laboral-td-parte .laboral-celda-vacio,
        .laboral-td-parte .laboral-dot,
        .laboral-td-parte .laboral-pill-asignado {
          vertical-align: middle;
        }
        .laboral-matriz-table thead .laboral-td-parte {
          background: #f1f3f5;
        }
        .laboral-celda-vacio { opacity: 0.45; }
        .laboral-dot {
          width: 1.35rem;
          height: 1.35rem;
          border-radius: 50%;
          font-size: 0.75rem;
          font-weight: 700;
          line-height: 1;
          cursor: help;
        }
        .laboral-dot--ok {
          background: #198754;
          color: #fff;
        }
        .laboral-dot--danger {
          background: #dc3545;
          color: #fff;
        }
        .laboral-pill-asignado {
          display: inline-block;
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          padding: 0.12rem 0.35rem;
          border-radius: 0.25rem;
          font-size: 0.72rem;
          font-weight: 700;
          background: #ffc107;
          color: #212529;
          cursor: help;
        }
        .laboral-matriz-table tbody tr:nth-child(even) .laboral-sticky-num,
        .laboral-matriz-table tbody tr:nth-child(even) .laboral-sticky-nom {
          background: #f8f9fa;
        }
        .laboral-celda-clickable {
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
          outline: none;
        }
        .laboral-celda-clickable:focus-visible {
          box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.35);
        }
        .laboral-detalle-overlay {
          position: fixed;
          inset: 0;
          z-index: 10050;
          background-color: rgba(0, 0, 0, 0.35);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
        }
        .laboral-detalle-dialog {
          width: 100%;
          max-width: 460px;
          background: #fff;
          border-radius: 10px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
          overflow: hidden;
        }
      `}</style>

      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div>
          <h5 className="mb-0">Vista laboral por tipo</h5>
          <small className="text-muted d-block">
            Tabla por tipo · columnas alineadas · filas por fecha de creación (más antiguos primero)
          </small>
          <div className="mt-2 small text-muted">
            <span className="me-2">
              <span className="laboral-dot laboral-dot--ok d-inline-flex me-1" style={{ width: '1rem', height: '1rem', fontSize: '0.65rem' }}>✓</span>
              Completado
            </span>
            <span className="me-2">
              <span className="laboral-pill-asignado me-1">NOMBRE</span>
              Asignado
            </span>
            <span>
              <span className="laboral-dot laboral-dot--danger d-inline-flex me-1" style={{ width: '1rem', height: '1rem', fontSize: '0.65rem' }}>!</span>
              Sin asignar
            </span>
          </div>
        </div>
        <input
          type="search"
          className="form-control form-control-sm"
          style={{ minWidth: '240px' }}
          placeholder="Buscar número, nombre, parte…"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          aria-label="Buscar en vista laboral"
        />
      </div>

      {loading && (
        <div className="text-center py-4 text-muted">
          <div className="spinner-border spinner-border-sm me-2" role="status" />
          Cargando matriz laboral…
        </div>
      )}

      {!loading && porTipoFiltrado.length === 0 && (
        <div className="alert alert-light border text-muted mb-0">
          No hay expedientes o ninguna fila coincide con la búsqueda.
        </div>
      )}

      {!loading &&
        porTipoFiltrado.map((grupo) => (
          <div key={grupo.tipoId} className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white py-3 border-bottom">
              <h6 className="mb-0 text-primary">{grupo.tipoNombre}</h6>
              <small className="text-muted">{grupo.filas.length} expediente(s)</small>
            </div>
            <div className="card-body p-0 laboral-matriz-scroll">
              <table className="table table-sm mb-0 laboral-matriz-table">
                <thead>
                  <tr>
                    <th className="laboral-sticky-num">Nº</th>
                    <th className="laboral-sticky-nom text-center">Nombre</th>
                    {(grupo.columnasParte || []).map((col) => (
                      <th key={col} className="laboral-td-parte text-center" title={col}>
                        {tituloColumnaParte(col)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {grupo.filas.map((row) => (
                    <tr key={row.expedienteId}>
                      <td className="laboral-sticky-num fw-semibold">
                        <Link
                          to={`/expedientes/${row.expedienteId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-decoration-none text-break d-block"
                        >
                          {row.numero}
                        </Link>
                      </td>
                      <td className="laboral-sticky-nom text-center">
                        <Link
                          to={`/expedientes/${row.expedienteId}?tab=notas`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-decoration-none text-dark"
                          title={`Abrir notas de ${row.clienteNombreCompleto || row.clientePrimerNombre || 'expediente'}`}
                        >
                          {row.clienteNombreCompleto || row.clientePrimerNombre || '—'}
                        </Link>
                      </td>
                      {(grupo.columnasParte || []).map((col) => (
                        <td key={col} className="laboral-td-parte text-center align-middle">
                          <CeldaParte
                            celda={row.celdas && row.celdas[col]}
                            onClick={(detalle) => setDetalleParte(detalle)}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}

      {modalDetalle && createPortal(modalDetalle, document.body)}
    </div>
  );
};

export default TablaLaboral;
